import React, { useState, useMemo } from 'react';
import { BacktestingEngine } from '../engines/BacktestingEngine.js';
import InteractiveChart from './InteractiveChart.jsx';

function BacktestingTab({ expirations }) {
  const [backtestParams, setBacktestParams] = useState({
    strategy: 'iron_condor',
    startDate: expirations[Math.min(200, expirations.length - 1)].date,
    endDate: expirations[0].date,
    initialCapital: 100000,
    maxRiskPerTrade: 0.02,
  });

  const [backtestResults, setBacktestResults] = useState(null);
  const [isRunning, setIsRunning] = useState(false);

  const runBacktest = () => {
    setIsRunning(true);
    
    setTimeout(() => {
      const engine = new BacktestingEngine(expirations);
      const results = engine.runBacktest(backtestParams.strategy, backtestParams);
      setBacktestResults(results);
      setIsRunning(false);
    }, 500);
  };

  const updateParam = (key, value) => {
    setBacktestParams(prev => ({ ...prev, [key]: value }));
  };

  // Chart data for equity curve
  const equityCurveData = useMemo(() => {
    if (!backtestResults) return [];
    return backtestResults.equity.map(e => ({
      date: e.date,
      value: e.value,
      label: e.date,
    }));
  }, [backtestResults]);

  // Monthly returns data
  const monthlyReturnsData = useMemo(() => {
    if (!backtestResults) return [];
    return Object.entries(backtestResults.monthlyReturns).map(([month, data]) => ({
      label: month,
      value: data.pnl,
      trades: data.trades,
    }));
  }, [backtestResults]);

  return (
    <div className="backtesting-tab">
      <div className="backtesting-header">
        <h2>📊 Backtesting - בדיקת אסטרטגיות</h2>
        <p className="tab-description">
          בדוק ביצועים היסטוריים של אסטרטגיות על נתונים אמיתיים
        </p>
      </div>

      {/* Configuration Panel */}
      <div className="backtest-config-panel">
        <h3>⚙️ הגדרות Backtest</h3>
        
        <div className="config-grid">
          <div className="config-item">
            <label>אסטרטגיה</label>
            <select
              value={backtestParams.strategy}
              onChange={(e) => updateParam('strategy', e.target.value)}
            >
              <option value="iron_condor">Iron Condor</option>
              <option value="bull_call_spread">Bull Call Spread</option>
              <option value="bear_put_spread">Bear Put Spread</option>
            </select>
          </div>

          <div className="config-item">
            <label>תאריך התחלה</label>
            <input
              type="date"
              value={backtestParams.startDate}
              onChange={(e) => updateParam('startDate', e.target.value)}
            />
          </div>

          <div className="config-item">
            <label>תאריך סיום</label>
            <input
              type="date"
              value={backtestParams.endDate}
              onChange={(e) => updateParam('endDate', e.target.value)}
            />
          </div>

          <div className="config-item">
            <label>הון התחלתי (₪)</label>
            <input
              type="number"
              value={backtestParams.initialCapital}
              onChange={(e) => updateParam('initialCapital', Number(e.target.value))}
              step="10000"
            />
          </div>

          <div className="config-item">
            <label>סיכון מקסימלי למסחר</label>
            <select
              value={backtestParams.maxRiskPerTrade}
              onChange={(e) => updateParam('maxRiskPerTrade', Number(e.target.value))}
            >
              <option value="0.01">1% - שמרני</option>
              <option value="0.02">2% - מתון</option>
              <option value="0.03">3% - אגרסיבי</option>
              <option value="0.05">5% - מאוד אגרסיבי</option>
            </select>
          </div>

          <button
            className="btn-run-backtest"
            onClick={runBacktest}
            disabled={isRunning}
          >
            {isRunning ? '⏳ רץ...' : '🚀 הרץ Backtest'}
          </button>
        </div>
      </div>

      {/* Results */}
      {backtestResults && (
        <>
          {/* Performance Metrics */}
          <div className="backtest-metrics-section">
            <h3>📈 תוצאות ביצועים</h3>
            <div className="metrics-grid-backtest">
              <div className="metric-card-backtest primary">
                <div className="metric-icon">💰</div>
                <div className="metric-content">
                  <div className="metric-label">תשואה כוללת</div>
                  <div className={`metric-value ${parseFloat(backtestResults.metrics.totalReturn) >= 0 ? 'positive' : 'negative'}`}>
                    {backtestResults.metrics.totalReturn >= 0 ? '+' : ''}{backtestResults.metrics.totalReturn}%
                  </div>
                  <div className="metric-detail">
                    {backtestResults.metrics.totalTrades} עסקאות
                  </div>
                </div>
              </div>

              <div className="metric-card-backtest">
                <div className="metric-icon">🎯</div>
                <div className="metric-content">
                  <div className="metric-label">אחוז רווח</div>
                  <div className="metric-value">{backtestResults.metrics.winRate}%</div>
                  <div className="metric-detail">Win Rate</div>
                </div>
              </div>

              <div className="metric-card-backtest">
                <div className="metric-icon">📊</div>
                <div className="metric-content">
                  <div className="metric-label">Sharpe Ratio</div>
                  <div className="metric-value">{backtestResults.metrics.sharpeRatio}</div>
                  <div className="metric-detail">Risk-Adjusted Return</div>
                </div>
              </div>

              <div className="metric-card-backtest danger">
                <div className="metric-icon">📉</div>
                <div className="metric-content">
                  <div className="metric-label">Max Drawdown</div>
                  <div className="metric-value negative">-{backtestResults.metrics.maxDrawdown}%</div>
                  <div className="metric-detail">Maximum Loss</div>
                </div>
              </div>

              <div className="metric-card-backtest">
                <div className="metric-icon">💵</div>
                <div className="metric-content">
                  <div className="metric-label">ממוצע רווח/הפסד</div>
                  <div className="metric-value">₪{backtestResults.metrics.avgPnL}</div>
                  <div className="metric-detail">Per Trade</div>
                </div>
              </div>

              <div className="metric-card-backtest">
                <div className="metric-icon">⚡</div>
                <div className="metric-content">
                  <div className="metric-label">Profit Factor</div>
                  <div className="metric-value">{backtestResults.metrics.profitFactor}</div>
                  <div className="metric-detail">Gross Profit / Gross Loss</div>
                </div>
              </div>
            </div>
          </div>

          {/* Equity Curve */}
          <div className="equity-curve-section">
            <h3>📈 עקומת הון (Equity Curve)</h3>
            <InteractiveChart
              data={equityCurveData}
              type="area"
              title="התפתחות ההון לאורך זמן"
            />
          </div>

          {/* Monthly Returns */}
          <div className="monthly-returns-section">
            <h3>📅 תשואות חודשיות</h3>
            <div className="monthly-returns-grid">
              {monthlyReturnsData.map((month, index) => (
                <div
                  key={index}
                  className={`monthly-card ${month.value >= 0 ? 'positive-month' : 'negative-month'}`}
                >
                  <div className="month-label">{month.label}</div>
                  <div className="month-pnl">
                    {month.value >= 0 ? '+' : ''}₪{month.value.toFixed(0)}
                  </div>
                  <div className="month-trades">{month.trades} עסקאות</div>
                </div>
              ))}
            </div>
          </div>

          {/* Trade Log */}
          <div className="trade-log-section">
            <h3>📋 יומן עסקאות</h3>
            <div className="trade-log-table-container">
              <table className="trade-log-table">
                <thead>
                  <tr>
                    <th>תאריך כניסה</th>
                    <th>תאריך יציאה</th>
                    <th>אסטרטגיה</th>
                    <th>מחיר כניסה</th>
                    <th>מחיר יציאה</th>
                    <th>רווח/הפסד</th>
                    <th>תשואה</th>
                  </tr>
                </thead>
                <tbody>
                  {backtestResults.trades.slice(-20).reverse().map((trade, index) => (
                    <tr key={index} className={trade.pnl >= 0 ? 'win-trade' : 'loss-trade'}>
                      <td>{trade.entryDate}</td>
                      <td>{trade.exitDate}</td>
                      <td>{trade.strategy}</td>
                      <td>{trade.entryPrice.toFixed(1)}</td>
                      <td>{trade.exitPrice.toFixed(1)}</td>
                      <td className={trade.pnl >= 0 ? 'positive' : 'negative'}>
                        {trade.pnl >= 0 ? '+' : ''}₪{trade.pnl.toFixed(0)}
                      </td>
                      <td className={trade.return >= 0 ? 'positive' : 'negative'}>
                        {trade.return >= 0 ? '+' : ''}{trade.return.toFixed(2)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {backtestResults.trades.length > 20 && (
                <div className="table-footer">
                  מוצגות 20 עסקאות אחרונות מתוך {backtestResults.trades.length}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {!backtestResults && !isRunning && (
        <div className="no-backtest-results">
          <div className="no-results-icon">📊</div>
          <h3>לא הורץ Backtest עדיין</h3>
          <p>הגדר פרמטרים למעלה ולחץ "הרץ Backtest" כדי לראות תוצאות</p>
        </div>
      )}
    </div>
  );
}

export default BacktestingTab;
